import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useUser } from '../contexts/UserContext';
import { apiService } from '../services/apiService';
import Monogram from '../components/layout/Monogram';
import Stamp from '../components/layout/Stamp';

interface Mentor {
  id: string;
  displayName: string;
  bio: string;
  university: string | null;
  major: string | null;
  classYear: string | null;
  mentorType?: string;
  occupation?: string | null;
  almaMater?: string | null;
  pricePerSession: number;
  isVerified: boolean;
  isTopRated: boolean;
  totalSessions: number;
  averageRating: number | null;
  rebookRate?: number | null;
  mentoringExperience?: string | null;
  resources?: Array<{
    id: string;
    type: string; // "video" | "document"
    title: string;
    description?: string | null;
    url: string;
    views: number;
  }>;
  achievements: Array<{
    id: string;
    category: string;
    title: string;
    icon: string | null;
    isPrimary: boolean;
    rank?: string | null;
    role?: string | null;
    childName?: string | null;
    verificationStatus?: string;
    verificationRecordId?: string | null;
  }>;
  specialties: Array<{ id: string; title: string; category: string }>;
  admissions: Array<{
    id: string;
    university: string;
    status?: string | null;
    isAttending: boolean;
    year?: number | null;
    childName?: string | null;
  }>;
  reviews: Array<{
    id: string;
    rating: number;
    title?: string | null;
    comment?: string | null;
    helpfulnessRating?: number | null;
    communicationRating?: number | null;
    knowledgeRating?: number | null;
    createdAt: string;
    student?: { id: string; name: string | null } | null;
  }>;
  packages?: Array<{
    id: string;
    name: string;
    description?: string | null;
    features: string[];
    sessionsCount: number;
    price: number;
  }>;
}

// Read-only star row, e.g. ★★★★★ with unfilled stars greyed out
const Stars: React.FC<{ value: number; size?: number }> = ({ value, size = 14 }) => {
  const filled = Math.round(Math.min(5, Math.max(0, value)));
  return (
    <span
      aria-label={`${value.toFixed(1)} out of 5 stars`}
      style={{ fontSize: size, letterSpacing: 2, whiteSpace: 'nowrap' }}
    >
      <span style={{ color: 'var(--cardinal)' }}>{'★'.repeat(filled)}</span>
      <span style={{ color: 'var(--hairline)' }}>{'★'.repeat(5 - filled)}</span>
    </span>
  );
};

// Clickable 1–5 star input for the review form
const StarPicker: React.FC<{ value: number; onChange: (v: number) => void }> = ({
  value,
  onChange,
}) => (
  <span style={{ whiteSpace: 'nowrap' }}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        aria-label={`${n} star${n !== 1 ? 's' : ''}`}
        onClick={() => onChange(n)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: 22,
          lineHeight: 1,
          padding: '0 2px',
          color: n <= value ? 'var(--cardinal)' : 'var(--hairline)',
        }}
      >
        ★
      </button>
    ))}
  </span>
);

const SUB_RATING_FIELDS = [
  { key: 'helpfulnessRating', label: 'Helpfulness' },
  { key: 'communicationRating', label: 'Communication' },
  { key: 'knowledgeRating', label: 'Knowledge' },
] as const;

const COMMENT_PREVIEW_LENGTH = 300;

const MentorProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const [mentor, setMentor] = useState<Mentor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Either 'hourly' (custom hourly session) or an offering (MentorPackage) id
  const [selectedPackage, setSelectedPackage] = useState<string>('hourly');
  const [remaining, setRemaining] = useState<{ remaining: number } | null>(null);
  // Reviews: expand/collapse long comments + write-a-review flow
  const [expandedReviews, setExpandedReviews] = useState<Record<string, boolean>>({});
  const [eligibleSessionId, setEligibleSessionId] = useState<string | null>(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    rating: 0,
    title: '',
    comment: '',
    helpfulnessRating: 0,
    communicationRating: 0,
    knowledgeRating: 0,
  });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  const fetchMentor = useCallback(async () => {
    try {
      setLoading(true);
      if (id === 'me') {
        const result = await apiService.get('/mentors/my-profile');
        setMentor(result.data || result);
      } else {
        const response = await axios.get(`${API_BASE_URL}/mentors/${id}`);
        setMentor(response.data.data || response.data);
      }
      setError(null);
    } catch (e) {
      console.error('Error fetching mentor:', e);
      setError('This mentor profile could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, id]);

  useEffect(() => {
    fetchMentor();
    window.scrollTo(0, 0);
  }, [fetchMentor]);

  // Mentors with offerings: pre-select the first one
  useEffect(() => {
    if (mentor?.packages && mentor.packages.length > 0) {
      setSelectedPackage(mentor.packages[0].id);
    }
  }, [mentor]);

  useEffect(() => {
    const check = async () => {
      if (!user || !mentor) return;
      const result = await apiService.getRemainingMentorSessions(mentor.id);
      if (result.success && result.remainingSessions > 0) {
        setRemaining({ remaining: result.remainingSessions });
      } else {
        setRemaining(null);
      }
    };
    check();
  }, [user, mentor]);

  // A signed-in family can review if they have a completed, not-yet-reviewed
  // session with this mentor (matches the backend's ownership rules).
  useEffect(() => {
    const checkReviewEligibility = async () => {
      if (!user || !mentor) {
        setEligibleSessionId(null);
        return;
      }
      try {
        const result = await apiService.get('/mentors/my-sessions');
        const sessions: any[] = result?.sessions || [];
        const eligible = sessions.find(
          (s) =>
            s.mentorProfileId === mentor.id &&
            s.status === 'completed' &&
            s.studentId === user.id &&
            !s.review
        );
        setEligibleSessionId(eligible ? eligible.id : null);
      } catch {
        setEligibleSessionId(null);
      }
    };
    checkReviewEligibility();
  }, [user, mentor]);

  const handleSubmitReview = async () => {
    if (!eligibleSessionId) return;
    if (reviewForm.rating < 1) {
      setReviewError('Please choose an overall star rating.');
      return;
    }
    setSubmittingReview(true);
    setReviewError(null);
    const payload: any = { rating: reviewForm.rating };
    if (reviewForm.title.trim()) payload.title = reviewForm.title.trim();
    if (reviewForm.comment.trim()) payload.comment = reviewForm.comment.trim();
    if (reviewForm.helpfulnessRating) payload.helpfulnessRating = reviewForm.helpfulnessRating;
    if (reviewForm.communicationRating)
      payload.communicationRating = reviewForm.communicationRating;
    if (reviewForm.knowledgeRating) payload.knowledgeRating = reviewForm.knowledgeRating;
    const result = await apiService.submitMentorReview(eligibleSessionId, payload);
    setSubmittingReview(false);
    if (result.success || result.error === 'already_reviewed') {
      setShowReviewForm(false);
      setEligibleSessionId(null);
      setReviewForm({
        rating: 0,
        title: '',
        comment: '',
        helpfulnessRating: 0,
        communicationRating: 0,
        knowledgeRating: 0,
      });
      fetchMentor();
    } else {
      setReviewError(result.error || 'Failed to submit review. Please try again.');
    }
  };

  const buildBookingMentor = (m: Mentor) => ({
    id: m.id,
    displayName: m.displayName,
    university: m.university,
    major: m.major,
    classYear: m.classYear,
    mentorType: m.mentorType || 'student',
    occupation: m.occupation || null,
    pricePerSession: m.pricePerSession,
    isVerified: m.isVerified,
    isTopRated: m.isTopRated,
  });

  const handleChooseTime = () => {
    if (!mentor) return;
    const customPkg = mentor.packages?.find((p) => p.id === selectedPackage);
    const state = customPkg
      ? {
          mentor: buildBookingMentor(mentor),
          packageType: 'custom' as const,
          packageId: customPkg.id,
          packagePrice: customPkg.price,
          sessionsCount: customPkg.sessionsCount,
          customPackage: {
            name: customPkg.name,
            description: customPkg.description || null,
            sessionsCount: customPkg.sessionsCount,
            price: customPkg.price,
          },
        }
      : {
          mentor: buildBookingMentor(mentor),
          packageType: 'single' as const,
          packagePrice: mentor.pricePerSession,
          sessionsCount: 1,
        };
    if (!user) {
      sessionStorage.setItem('intendedBooking', JSON.stringify(state));
      navigate('/login');
      return;
    }
    navigate('/mentor-booking', { state });
  };

  const handleUseCredit = () => {
    if (!mentor) return;
    navigate('/mentor-booking', {
      state: {
        mentor: buildBookingMentor(mentor),
        packageType: 'single' as const,
        packagePrice: 0,
        sessionsCount: 1,
        scheduleOnly: true,
      },
    });
  };

  if (loading) {
    return (
      <div className="section" style={{ paddingTop: 40 }}>
        <div className="wrap">
          <p className="meta">Loading profile…</p>
        </div>
      </div>
    );
  }

  if (error || !mentor) {
    return (
      <div className="section" style={{ paddingTop: 40 }}>
        <div className="wrap">
          <p className="meta">{error || 'Mentor not found.'}</p>
          <Link className="btn btn-secondary btn-sm" to="/mentors" style={{ marginTop: 16 }}>
            ← Back to browse
          </Link>
        </div>
      </div>
    );
  }

  const isParent = mentor.mentorType === 'parent';
  const credLine = isParent
    ? [mentor.occupation || 'Parent mentor', mentor.almaMater ? `${mentor.almaMater} alum` : null]
        .filter(Boolean)
        .join(' · ')
    : [
        mentor.university && mentor.classYear
          ? `${mentor.university} '${String(mentor.classYear).slice(-2)}`
          : mentor.university,
        mentor.major,
      ]
        .filter(Boolean)
        .join(' · ');

  const admitted = mentor.admissions?.filter((a) => a.isAttending).length || 0;

  const reviews = mentor.reviews || [];
  const overallRating =
    mentor.averageRating ??
    (reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : null);
  const subRatingBars = SUB_RATING_FIELDS.map(({ key, label }) => {
    const values = reviews
      .map((r) => r[key])
      .filter((v): v is number => typeof v === 'number');
    return {
      label,
      avg: values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : null,
    };
  }).filter((b) => b.avg !== null);

  return (
    <div className="section" style={{ paddingTop: 40 }}>
      <div className="wrap">
        <Link className="meta" to="/mentors">
          ← Back to browse
        </Link>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 360px',
            gap: 48,
            alignItems: 'start',
            marginTop: 24,
          }}
        >
          <div>
            {/* Header */}
            <div style={{ display: 'flex', gap: 24, alignItems: 'center', marginBottom: 28 }}>
              <Monogram name={mentor.displayName} lg />
              <div>
                <h1 style={{ fontSize: 32 }}>{mentor.displayName}</h1>
                {credLine && (
                  <p className="cred" style={{ marginTop: 4 }}>
                    {credLine}
                  </p>
                )}
                <p className="meta" style={{ marginTop: 4 }}>
                  {[
                    mentor.totalSessions > 0 ? `${mentor.totalSessions} sessions` : 'New mentor',
                    mentor.averageRating ? `${mentor.averageRating.toFixed(1)} ★ average` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
            </div>

            {/* Credentials */}
            <div className="card" style={{ marginBottom: 24 }}>
              <h3 style={{ marginBottom: 4 }}>Credentials</h3>
              <p className="meta" style={{ marginBottom: 16 }}>
                Each claim shows its check against the official record.{' '}
                <Link to="/data#method" style={{ textDecoration: 'underline' }}>
                  How verification works
                </Link>
              </p>
              {mentor.achievements && mentor.achievements.length > 0 ? (
                <table className="data">
                  <tbody>
                    {mentor.achievements.map((a) => (
                      <tr key={a.id}>
                        <td style={{ fontWeight: 500 }}>{a.category}</td>
                        <td>
                          {a.title}
                          {a.rank && !a.title.toLowerCase().includes(a.rank.toLowerCase())
                            ? ` — ${a.rank}`
                            : ''}
                          {a.childName ? ` (${a.childName})` : ''}
                          {a.role && (
                            <span className="meta" style={{ display: 'block', marginTop: 2 }}>
                              Role: {a.role}
                            </span>
                          )}
                        </td>
                        <td>
                          {a.verificationStatus === 'verified' && (
                            <>
                              <Stamp variant="verified">✓ Verified</Stamp>
                              {a.verificationRecordId && (
                                <span className="meta" style={{ display: 'block', marginTop: 4 }}>
                                  Record {a.verificationRecordId}
                                </span>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="meta">Credentials under review.</p>
              )}
            </div>

            {/* Application grid */}
            {mentor.admissions && mentor.admissions.length > 0 && (
              <div className="card" style={{ marginBottom: 24 }}>
                <h3 style={{ marginBottom: 4 }}>
                  {isParent ? "Children's outcomes" : 'Full application grid'}
                </h3>
                <p className="meta" style={{ marginBottom: 16 }}>
                  {isParent
                    ? 'Where their kids applied and enrolled.'
                    : `Required for listing. ${mentor.admissions.length} school${
                        mentor.admissions.length !== 1 ? 's' : ''
                      } — attending ${admitted > 0 ? '1' : '—'}.`}
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {mentor.admissions.map((adm) => {
                    const status = (adm.status || 'admitted').toLowerCase();
                    const chip = status.startsWith('reject')
                      ? 'reject'
                      : status.startsWith('waitlist')
                      ? 'waitlist'
                      : 'admit';
                    return (
                      <span key={adm.id} className={`grid-chip ${chip}`}>
                        {adm.university}
                        {adm.year ? ` '${String(adm.year).slice(-2)}` : ''}
                        {adm.childName ? ` (${adm.childName})` : ''}
                        {adm.isAttending ? ' — attending' : ''}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* About / sessions */}
            <div className="card" style={{ marginBottom: 24 }}>
              <h3 style={{ marginBottom: 12 }}>What a session covers</h3>
              {mentor.bio && (
                <p style={{ fontSize: 15, marginBottom: 12 }}>“{mentor.bio}”</p>
              )}
              {mentor.specialties && mentor.specialties.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {mentor.specialties.map((s) => (
                    <span key={s.id} className="tag">
                      {s.title}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Resources — only shown for mentors who have shared resources */}
            {mentor.resources && mentor.resources.length > 0 && (
              <div className="card" style={{ marginBottom: 24 }}>
                <h3 style={{ marginBottom: 4 }}>
                  {mentor.displayName.split(' ')[0]}'s resources
                </h3>
                <p className="meta" style={{ marginBottom: 16 }}>
                  Videos and guides {mentor.displayName.split(' ')[0]} has shared with CommonRoom
                  families
                </p>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: 16,
                  }}
                >
                  {mentor.resources.map((r) => (
                    <a
                      key={r.id}
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        gap: 12,
                        textDecoration: 'none',
                        color: 'inherit',
                        alignItems: 'flex-start',
                      }}
                    >
                      <div
                        style={{
                          width: 92,
                          height: 60,
                          flexShrink: 0,
                          borderRadius: 6,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: r.type === 'video' ? 18 : 12,
                          fontWeight: 600,
                          letterSpacing: r.type === 'video' ? 0 : 1,
                          ...(r.type === 'video'
                            ? { background: 'var(--cardinal)', color: 'var(--on-cardinal)' }
                            : { border: '1px solid var(--hairline)', color: 'var(--cardinal)' }),
                        }}
                      >
                        {r.type === 'video' ? '▶' : 'PDF'}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <b style={{ fontWeight: 600, fontSize: 14, display: 'block' }}>{r.title}</b>
                        {r.description && (
                          <span className="meta" style={{ display: 'block', marginTop: 2 }}>
                            {r.description}
                          </span>
                        )}
                        <span className="meta" style={{ display: 'block', marginTop: 4 }}>
                          {r.type === 'video' ? 'Video' : 'Document'} · {r.views} view
                          {r.views !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Mentoring experience — only shown when the mentor has filled it in */}
            {mentor.mentoringExperience && (
              <div className="card" style={{ marginBottom: 24 }}>
                <h3 style={{ marginBottom: 12 }}>
                  {mentor.displayName.split(' ')[0]}'s mentoring experience
                </h3>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                  {mentor.totalSessions > 0 && (
                    <span className="tag">{mentor.totalSessions} sessions completed</span>
                  )}
                  {mentor.isTopRated && <span className="tag">Top-rated mentor</span>}
                  {mentor.rebookRate != null && mentor.rebookRate > 0 && (
                    <span className="tag">{Math.round(mentor.rebookRate)}% of families rebook</span>
                  )}
                </div>
                <p style={{ fontSize: 15 }}>{mentor.mentoringExperience}</p>
              </div>
            )}

            {/* Reviews */}
            <div className="card">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 16,
                }}
              >
                <h3>
                  {reviews.length > 0
                    ? `${reviews.length} review${reviews.length !== 1 ? 's' : ''}`
                    : 'Reviews'}
                </h3>
                {eligibleSessionId && !showReviewForm && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowReviewForm(true)}
                  >
                    Write a review
                  </button>
                )}
              </div>

              {/* Write-a-review form — only for families with a completed, unreviewed session */}
              {eligibleSessionId && showReviewForm && (
                <div
                  style={{
                    border: '1px solid var(--hairline)',
                    borderRadius: 8,
                    padding: 16,
                    marginBottom: 20,
                  }}
                >
                  <div className="field">
                    <label>Overall rating</label>
                    <StarPicker
                      value={reviewForm.rating}
                      onChange={(v) => setReviewForm((f) => ({ ...f, rating: v }))}
                    />
                  </div>
                  <div className="field">
                    <label>Title</label>
                    <input
                      type="text"
                      maxLength={100}
                      placeholder="Sum up your experience in one line"
                      value={reviewForm.title}
                      onChange={(e) =>
                        setReviewForm((f) => ({ ...f, title: e.target.value }))
                      }
                    />
                  </div>
                  <div className="field">
                    <label>Your review</label>
                    <textarea
                      rows={4}
                      placeholder="What did your family work on, and how did it go?"
                      value={reviewForm.comment}
                      onChange={(e) =>
                        setReviewForm((f) => ({ ...f, comment: e.target.value }))
                      }
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 12 }}>
                    {SUB_RATING_FIELDS.map(({ key, label }) => (
                      <div key={key}>
                        <label
                          className="meta"
                          style={{ display: 'block', marginBottom: 2 }}
                        >
                          {label}
                        </label>
                        <StarPicker
                          value={reviewForm[key]}
                          onChange={(v) => setReviewForm((f) => ({ ...f, [key]: v }))}
                        />
                      </div>
                    ))}
                  </div>
                  {reviewError && (
                    <p className="meta" style={{ color: 'var(--cardinal)', marginBottom: 8 }}>
                      {reviewError}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={submittingReview}
                      onClick={handleSubmitReview}
                    >
                      {submittingReview ? 'Submitting…' : 'Submit review'}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      disabled={submittingReview}
                      onClick={() => {
                        setShowReviewForm(false);
                        setReviewError(null);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Summary: overall rating + sub-rating bars */}
              {reviews.length > 0 && overallRating != null && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '150px 1fr',
                    gap: 32,
                    alignItems: 'center',
                    paddingBottom: 20,
                    marginBottom: 20,
                    borderBottom: '1px solid var(--hairline)',
                  }}
                >
                  <div>
                    <p className="meta">Overall rating</p>
                    <p style={{ fontSize: 36, fontWeight: 600, lineHeight: 1.15 }}>
                      {overallRating.toFixed(1)}
                    </p>
                    <Stars value={overallRating} size={16} />
                  </div>
                  {subRatingBars.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {subRatingBars.map((b) => (
                        <div
                          key={b.label}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '120px 1fr 32px',
                            gap: 12,
                            alignItems: 'center',
                          }}
                        >
                          <span className="meta">{b.label}</span>
                          <span
                            style={{
                              display: 'block',
                              height: 6,
                              borderRadius: 3,
                              background: 'var(--hairline)',
                              overflow: 'hidden',
                            }}
                          >
                            <span
                              style={{
                                display: 'block',
                                height: '100%',
                                width: `${(b.avg! / 5) * 100}%`,
                                background: 'var(--cardinal)',
                              }}
                            />
                          </span>
                          <span className="meta" style={{ textAlign: 'right' }}>
                            {b.avg!.toFixed(1)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Review entries */}
              {reviews.length > 0 ? (
                reviews.map((r, i) => {
                  const text = r.comment || '';
                  const isLong = text.length > COMMENT_PREVIEW_LENGTH;
                  const isExpanded = !!expandedReviews[r.id];
                  const shownText =
                    isLong && !isExpanded
                      ? `${text.slice(0, COMMENT_PREVIEW_LENGTH).trimEnd()}…`
                      : text;
                  const reviewerName = r.student?.name
                    ? r.student.name.split(' ')[0]
                    : 'Anonymous';
                  const when = r.createdAt
                    ? new Date(r.createdAt).toLocaleDateString('en-US', {
                        month: 'long',
                        year: 'numeric',
                      })
                    : null;
                  return (
                    <div
                      key={r.id}
                      style={
                        i < reviews.length - 1
                          ? {
                              borderBottom: '1px solid var(--hairline)',
                              paddingBottom: 16,
                              marginBottom: 16,
                            }
                          : undefined
                      }
                    >
                      <p style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Stars value={r.rating} />
                        <span className="meta">{r.rating.toFixed(1)}</span>
                      </p>
                      {r.title && (
                        <p style={{ fontWeight: 600, fontSize: 15, marginTop: 6 }}>{r.title}</p>
                      )}
                      {text && (
                        <p style={{ fontSize: 15, marginTop: 6 }}>
                          {shownText}
                          {isLong && (
                            <>
                              {' '}
                              <button
                                onClick={() =>
                                  setExpandedReviews((prev) => ({
                                    ...prev,
                                    [r.id]: !isExpanded,
                                  }))
                                }
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  color: 'var(--cardinal)',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  font: 'inherit',
                                }}
                              >
                                {isExpanded ? 'View less' : 'View more'}
                              </button>
                            </>
                          )}
                        </p>
                      )}
                      <p className="meta" style={{ marginTop: 8 }}>
                        {[reviewerName, when, 'Verified booking'].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                  );
                })
              ) : (
                <p className="meta">No reviews yet — be the first family to book.</p>
              )}
            </div>
          </div>

          {/* Booking panel */}
          <aside className="card" style={{ position: 'sticky', top: 88 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 4,
              }}
            >
              <span className="stat">
                <span className="num plain" style={{ fontSize: 26 }}>
                  ${mentor.pricePerSession}
                </span>
              </span>
              <span className="meta">/ hour</span>
            </div>
            <p className="meta" style={{ marginBottom: 16 }}>
              Sessions are recorded with consent · notes included
            </p>

            {remaining && (
              <div className="note" style={{ marginBottom: 16 }}>
                You have <b style={{ fontWeight: 600 }}>{remaining.remaining}</b> pack credit
                {remaining.remaining !== 1 ? 's' : ''} with this mentor.{' '}
                <button
                  onClick={handleUseCredit}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: 'var(--cardinal)',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    font: 'inherit',
                  }}
                >
                  Schedule with a credit →
                </button>
              </div>
            )}

            <div className="field">
              <label>{mentor.displayName.split(' ')[0]}'s offerings</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(mentor.packages || []).map((pkg) => (
                  <label
                    key={pkg.id}
                    className={`radio-tile${selectedPackage === pkg.id ? ' on' : ''}`}
                  >
                    <input
                      type="radio"
                      name="sess"
                      checked={selectedPackage === pkg.id}
                      onChange={() => setSelectedPackage(pkg.id)}
                    />
                    <span>
                      <b style={{ fontWeight: 600 }}>
                        {pkg.name} — ${pkg.price}
                      </b>
                      <br />
                      <span className="meta">
                        {pkg.sessionsCount} session{pkg.sessionsCount !== 1 ? 's' : ''} · 60 min
                        each
                        {pkg.description ? ` · ${pkg.description}` : ''}
                      </span>
                      {pkg.features && pkg.features.length > 0 && (
                        <ul className="meta" style={{ margin: '6px 0 0', paddingLeft: 16 }}>
                          {pkg.features.map((f, i) => (
                            <li key={i}>{f}</li>
                          ))}
                        </ul>
                      )}
                    </span>
                  </label>
                ))}
                <label
                  className={`radio-tile${selectedPackage === 'hourly' ? ' on' : ''}`}
                >
                  <input
                    type="radio"
                    name="sess"
                    checked={selectedPackage === 'hourly'}
                    onChange={() => setSelectedPackage('hourly')}
                  />
                  <span>
                    <b style={{ fontWeight: 600 }}>
                      Custom hourly — ${mentor.pricePerSession}/hr
                    </b>
                    <br />
                    <span className="meta">
                      60-minute session · bring your own questions — interviews, planning,
                      anything
                    </span>
                  </span>
                </label>
              </div>
            </div>

            <button className="btn btn-primary btn-block" onClick={handleChooseTime}>
              Choose a time
            </button>
            <button
              className="btn btn-secondary btn-block"
              style={{ marginTop: 8 }}
              onClick={() => navigate(`/intro-call/${mentor.id}`)}
            >
              Book a free 15-min intro call
            </button>
            <p className="meta" style={{ marginTop: 12 }}>
              Sessions are recorded with consent. For students 13–15, a parent joins every session
              — mentors see and follow this rule.
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default MentorProfilePage;
